import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { UserSportStatisticSlug } from '@sorare/core/src/__generated__/globalTypes';
import { ShopItemType } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { AutoSaveInput } from '@sorare/core/src/atoms/inputs/AutoSaveInput';
import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { FormLabel } from '@sorare/core/src/components/form/FormLabel';
import { SETTINGS_ACCOUNT } from '@sorare/core/src/constants/__generated__/routes';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { range } from '@sorare/core/src/lib/arrays';
import { userAttributes } from '@sorare/core/src/lib/glossary';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import { Banner } from '#src/components/profile/Header/Banner/index.tsx';
import { Logo } from '#src/components/profile/Header/Logo/index.tsx';
import { FormattedStatistic } from '#src/components/profile/ManagerIDHeader/FormattedStatistic/index.tsx';
import { ShopItemDialog } from '#src/components/shopItems/ShopItemDialog/index.tsx';

import type { ClubEditor_userSportProfile } from './__generated__/index.graphql.ts';

const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 2;
  right: var(--double-unit);
  top: var(--double-unit);
`;
const DialogRoot = styled(Vertical)`
  padding: var(--double-unit);
`;
const Row = styled(Horizontal)`
  & > * {
    flex: 1;
  }
`;
const StatisticsRow = styled(Row)`
  flex-direction: column;
  align-items: stretch;

  @media ${mobileAndAbove} {
    flex-direction: row;
  }
`;
const StyledInput = styled(AutoSaveInput)`
  background-color: var(--c-nd-100);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  clubEdition?: ReactNode;
  onSubmit: (data: {
    clubName?: string;
    selectedStatistics?: (UserSportStatisticSlug | null)[];
    backgroundId?: string;
    clubBadgeId?: string;
    achievementBadgesIds?: (string | null)[];
  }) => void;
  userSportProfile?: ClubEditor_userSportProfile;
  clubName?: string;
};

export const ClubEditor = ({
  open,
  onClose,
  userSportProfile,
  clubName,
  clubEdition,
  onSubmit,
}: Props) => {
  const { isIosApp } = useIsMobileApp();

  const [editingAchievmentBadgeIndex, setEditingAchievmentBadgeIndex] =
    useState<number>();
  const [pickingSkin, setPickingSkin] = useState<
    typeof ShopItemType.BACKGROUND | typeof ShopItemType.CLUB_BADGE
  >();

  const statisticsOptions = useMemo(() => {
    return (
      userSportProfile?.userSportStatistics?.map(stat => ({
        label: (
          <Vertical gap={0} as="span">
            <LabelS color="var(--c-nd-600)">{stat.title}</LabelS>
            <BodyS bold color="var(--c-white)">
              <FormattedStatistic statistic={stat} />
            </BodyS>
          </Vertical>
        ),
        value: stat.name,
      })) || []
    );
  }, [userSportProfile?.userSportStatistics]);

  return (
    <Dialog
      fullScreen={isIosApp}
      maxWidth="sm"
      open={open}
      onClose={onClose}
      hideHeader
    >
      {({ CloseButton }) => (
        <>
          <CloseButtonWrapper>
            <CloseButton onClose={onClose} />
          </CloseButtonWrapper>
          {clubEdition}
          <>
            {!clubEdition && !!userSportProfile && (
              <>
                <Banner
                  pictureUrl={userSportProfile.background?.pictureUrl}
                  alt=""
                  onClick={() => setPickingSkin(ShopItemType.BACKGROUND)}
                />
                <Logo
                  overlap
                  pictureUrl={userSportProfile.clubBadge?.pictureUrl}
                  alt=""
                  onClick={() => setPickingSkin(ShopItemType.CLUB_BADGE)}
                />
              </>
            )}
            <DialogRoot gap={3}>
              <Vertical>
                <FormLabel id="selectedStatistics">
                  <FormattedMessage {...userAttributes.clubName} />
                </FormLabel>
                <StyledInput
                  name="clubName"
                  maxLength={100}
                  defaultValue={clubName || userSportProfile?.clubName || ''}
                  onDebouncedChange={async value =>
                    onSubmit({ clubName: value?.toString().trim() })
                  }
                />
              </Vertical>
              {!clubEdition && !!userSportProfile && (
                <>
                  <Vertical>
                    <FormLabel id="selectedStatistics">
                      <FormattedMessage
                        id="Manager.Header.ClubEditor.myStatistics"
                        defaultMessage="My statistics"
                      />
                    </FormLabel>
                    <StatisticsRow>
                      {range(3).map(i => (
                        <DropdownSelect
                          // eslint-disable-next-line react/no-array-index-key
                          key={i}
                          size="medium"
                          value={
                            userSportProfile.selectedUserSportStatistics?.[i]
                              ?.name
                          }
                          options={statisticsOptions}
                          onChange={value => {
                            const badgesSlugs = [
                              ...(userSportProfile.selectedUserSportStatistics?.map(
                                badge => badge?.name || null
                              ) || []),
                            ];
                            badgesSlugs[i] = value;
                            onSubmit({ selectedStatistics: badgesSlugs });
                          }}
                        />
                      ))}
                    </StatisticsRow>
                  </Vertical>
                  <Vertical>
                    <FormLabel id="selectedStatistics">
                      <FormattedMessage
                        id="Manager.Header.ClubEditor.myBadges"
                        defaultMessage="My badges"
                      />
                    </FormLabel>
                    <Row>
                      {range(3).map(i => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Vertical center key={i}>
                          <div>
                            <Logo
                              pictureUrl={
                                userSportProfile.achievementBadges?.[i]
                                  ?.pictureUrl
                              }
                              alt=""
                              onClick={() => setEditingAchievmentBadgeIndex(i)}
                            />
                          </div>
                          <LabelS as="p" color="var(--c-nd-600)">
                            <FormattedMessage
                              id="Manager.Header.ClubEditor.badgeIndex"
                              defaultMessage="Badge {index}"
                              values={{ index: i + 1 }}
                            />
                          </LabelS>
                        </Vertical>
                      ))}
                    </Row>
                  </Vertical>
                </>
              )}
              {!isIosApp && (
                <Link to={SETTINGS_ACCOUNT} onClick={onClose}>
                  <SBHorizontal>
                    <Vertical gap={0.5}>
                      <BodyM as="p" bold>
                        <FormattedMessage
                          id="ManagerProfile.Header.moreSettings"
                          defaultMessage="More settings"
                        />
                      </BodyM>
                      <LabelS as="p" color="var(--c-nd-600)">
                        <FormattedMessage
                          id="ManagerProfile.Header.updateYourAvatarUsernameAndMore"
                          defaultMessage="Update your avatar, username, socials and more."
                        />
                      </LabelS>
                    </Vertical>
                    <FontAwesomeIcon
                      icon={faArrowUpRightFromSquare}
                      color="var(--c-nd-600)"
                    />
                  </SBHorizontal>
                </Link>
              )}
            </DialogRoot>
            {!!(pickingSkin || editingAchievmentBadgeIndex !== undefined) && (
              <ShopItemDialog
                open
                onClose={() => {
                  setPickingSkin(undefined);
                  setEditingAchievmentBadgeIndex(undefined);
                }}
                type={pickingSkin || ShopItemType.ACHIEVEMENT_BADGE}
                onSelect={({ id }) => {
                  let payload;
                  if (pickingSkin === ShopItemType.BACKGROUND) {
                    payload = { backgroundId: id };
                  } else if (pickingSkin === ShopItemType.CLUB_BADGE) {
                    payload = { clubBadgeId: id };
                  } else if (editingAchievmentBadgeIndex !== undefined) {
                    const newBadgesIds = [
                      ...(userSportProfile?.achievementBadges?.map(
                        badge => badge?.id || null
                      ) || []),
                    ];
                    newBadgesIds[editingAchievmentBadgeIndex] = id;
                    payload = { achievementBadgesIds: newBadgesIds };
                  }

                  if (payload) {
                    onSubmit(payload);
                    setPickingSkin(undefined);
                    setEditingAchievmentBadgeIndex(undefined);
                  }
                }}
              />
            )}
          </>
        </>
      )}
    </Dialog>
  );
};

ClubEditor.fragments = {
  userSportProfile: gql`
    fragment ClubEditor_userSportProfile on UserSportProfileInterface {
      id
      clubName
      background {
        id
        name
        pictureUrl
      }
      clubBadge {
        id
        name
        pictureUrl
      }
      achievementBadges {
        id
        name
        pictureUrl
      }
      selectedUserSportStatistics {
        id
        name
        title
        ...FormattedStatistic_UserSportStatistic
      }
      userSportStatistics {
        id
        name
        title
        ...FormattedStatistic_UserSportStatistic
      }
    }
    ${FormattedStatistic.fragments.userSportStatistic}
  ` as TypedDocumentNode<ClubEditor_userSportProfile>,
};
